<template>
  <section page-gate>
    <div class="container">
      <div class="flex min-h-96 justify-center items-center flex-col gap-5">
        <h1 class="section-title text-3xl" v-text="pageStore?.page?.name"></h1>
        <div v-if="errorMessage" class="error-message" role="alert">
          {{ errorMessage }}
        </div>
        <template v-if="forbidden">
          <h2 class="section-lead">Access Forbidden</h2>
          <p class="section-description">Please use a valid Google Account</p>
          <p class="section-description">
            <button type="button" @click="refresh" class="btn btn-primary btn-sm">
              Retry Login
            </button>
          </p>
        </template>
        <div v-else class="block">
          <GoogleSignInButton :one-tap="true" :auto-select="autoLogin" context="signin" @success="handleLoginSuccess" @error="handleLoginError"></GoogleSignInButton>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
  import type { CredentialResponse } from "vue3-google-signin";
  import { decodeCredential } from "vue3-google-signin";
  import { useAuthStore } from "@/stores/auth";

  const { setItem } = useLocalStorage();
  const authStore = useAuthStore();
  const pageStore = usePageStore();
  const forbidden = ref(false);
  const autoLogin = ref(false);

  const domainWhitelist = ["kudosity.com", "kudosity.co", "burstsms.com"];
  const config = useRuntimeConfig();
  const emailWhitelist = config.public.EMAIL_WHITELIST?.split(',').map((email: string) => email.trim()).filter(Boolean) || [];

  const emit = defineEmits(["authenticated"]);

  const refresh = () => {
    window.location.reload();
  };

  interface DecodedCredential {
    email: string;
    email_verified?: boolean;
    hd?: string;
    family_name?: string;
    given_name?: string;
    name?: string;
    picture?: string;
    id: string;
    iat?: number;
    exp?: number;
  }

  const isEmailAllowed = (email: string, domain: string): boolean => {
    // Check if email is in whitelist
    if (emailWhitelist.length > 0 && emailWhitelist.includes(email)) {
      return true;
    }
    // Check if domain is in whitelist (only if domain exists)
    return domain ? domainWhitelist.includes(domain) : false;
  };

  const errorMessage = ref<string | null>(null);

  const handleLoginSuccess = (response: CredentialResponse) => {
    const { credential } = response;
    if (!credential) return;

    const decodedCredential = decodeCredential(credential) as DecodedCredential | null;

    // Proper validation
    if (!decodedCredential?.email) {
      console.error("Invalid credential: missing email");
      forbidden.value = true;
      errorMessage.value = "Invalid credentials received";
      return;
    }

    // Log only in development
    if (import.meta.dev) {
      console.log("User:", decodedCredential);
    }

    // Fixed: handle undefined domain gracefully
    if (!isEmailAllowed(decodedCredential.email, decodedCredential?.hd || '')) {
      forbidden.value = true;
      errorMessage.value = "Your email domain is not authorized for access";
      return;
    }

    authStore.setToken(credential);

    // Ensure all required fields are present
    const user = {
      email: decodedCredential.email,
      email_verified: decodedCredential.email_verified ?? false,
      hd: decodedCredential.hd ?? '',
      family_name: decodedCredential.family_name ?? '',
      given_name: decodedCredential.given_name ?? '',
      name: decodedCredential.name ?? '',
      picture: decodedCredential.picture ?? '',
      id: decodedCredential.id,
      iat: decodedCredential.iat ?? 0,
      exp: decodedCredential.exp ?? 0,
    };

    authStore.setUser(user);

    // Fixed typo: use decodedCredential consistently
    setItem(
      "kudonauts",
      [decodedCredential.id, decodedCredential.iat ?? 0, decodedCredential.exp ?? 0].join('-')
    );

    emit("authenticated", true);
  };

  // handle an error event
  const handleLoginError = () => {
    console.error("Login failed");
    errorMessage.value = "Login failed. Please try again.";
  };

  onMounted(() => {
    if (authStore.isAuthenticated() === true) {
      emit("authenticated", true);
    }
  });
</script>
